const DEFAULT_EMPTY_VALUE = "-";

export const GPT_MODEL_COMPARE_SECTIONS = [
  {
    key: "identity",
    title: "Identity",
    fields: [
      { key: "typeLLM", label: "Model Type", type: "text" },
      { key: "modelDisplay", label: "Model ID", type: "text" },
      { key: "modelName", label: "Model Name", type: "text-diff" },
      { key: "description", label: "Description", type: "multiline" },
      { key: "versioning", label: "Version", type: "text" },
      { key: "isPublish", label: "Published", type: "boolean" },
    ],
  },
  {
    key: "configuration",
    title: "Model Configuration",
    fields: [
      { key: "provider", label: "Provider", type: "text" },
      { key: "model", label: "Model", type: "text" },
      { key: "extension", label: "Extension", type: "text-diff" },
      { key: "reasoningEffort", label: "Reasoning Effort", type: "text" },
    ],
  },
  {
    key: "prompting",
    title: "Prompting",
    fields: [
      { key: "promptTemplate", label: "Prompt Template", type: "multiline" },
      { key: "stop", label: "Stop Sequence", type: "multiline" },
    ],
  },
  {
    key: "generation",
    title: "Generation Settings",
    fields: [
      { key: "gender", label: "Gender", type: "text" },
      { key: "temperature", label: "Temperature", type: "number" },
      { key: "topP", label: "Top P", type: "number" },
      { key: "maxTokens", label: "Max Tokens", type: "number" },
      { key: "frequencyPenalty", label: "Frequency Penalty", type: "number" },
      { key: "presencePenalty", label: "Presence Penalty", type: "number" },
      { key: "isStream", label: "Streaming", type: "boolean" },
      { key: "isFeedback", label: "Feedback Enabled", type: "boolean" },
      { key: "isAllowImage", label: "Allow Image", type: "boolean" },
      { key: "fileUploadLimit", label: "File Upload Limit", type: "number" },
      { key: "isAllowLLMChange", label: "Allow LLM Change", type: "boolean" },
    ],
  },
  {
    key: "memory",
    title: "Memory and Context",
    fields: [
      { key: "historyCount", label: "History Count", type: "number" },
      { key: "historyMinute", label: "History Minute", type: "number" },
      { key: "Len_ShortMemory", label: "Short Memory Length", type: "number" },
      { key: "IsLongTerm_Memory", label: "Long Term Memory", type: "boolean" },
      { key: "IsChainMassage", label: "Chain Message", type: "boolean" },
      { key: "LenChainMassage", label: "Chain Length", type: "number" },
      { key: "TimeSecondChainMassage", label: "Chain Window Seconds", type: "number" },
      { key: "maximumChatInput", label: "Maximum Chat Input", type: "number" },
      { key: "typingSpeed", label: "Typing Speed", type: "number" },
    ],
  },
  {
    key: "knowledge",
    title: "Knowledge and Integration",
    fields: [
      { key: "vectordbName", label: "Vector DB", type: "array" },
      { key: "topVDB", label: "Top VDB", type: "number" },
      { key: "Cosine", label: "Cosine Similarity", type: "number" },
      { key: "productGroupID", label: "Product Group", type: "array" },
      { key: "productTopVDB", label: "Product Top VDB", type: "number" },
      { key: "productCosine", label: "Product Cosine", type: "number" },
      { key: "groupReplaceID", label: "Replace Group", type: "text" },
      { key: "DataIntegrationID", label: "Data Integration", type: "text" },
      { key: "SqlModelID", label: "SQL Model", type: "text" },
      { key: "promptSqlNoData", label: "SQL No Data Prompt", type: "multiline" },
      { key: "IntentGroupID", label: "Intent Group", type: "text" },
      { key: "flowGroupID", label: "Flow Group", type: "text" },
      { key: "guardRailModelID", label: "Guard Rail Model", type: "text" },
    ],
  },
  {
    key: "monitoring",
    title: "Monitoring and Guardrails",
    fields: [
      { key: "monitorLineUserID", label: "Monitor Line User", type: "text" },
      { key: "monitorSocialID", label: "Monitor Social", type: "text" },
      { key: "isShowNameUserMonitor", label: "Show Monitor Name", type: "boolean" },
      { key: "forbiddenWord", label: "Forbidden Word", type: "list-text" },
      { key: "loadingBarFlag", label: "Loading Bar", type: "boolean" },
      { key: "loadingBarDuration", label: "Loading Bar Duration", type: "number" },
      { key: "enableTime", label: "Enable Time", type: "boolean" },
      { key: "timeZone", label: "Time Zone", type: "text" },
    ],
  },
  {
    key: "image",
    title: "Image Settings",
    fields: [
      { key: "styleImage", label: "Style Image", type: "text" },
      { key: "resFormatImage", label: "Response Format", type: "text" },
      { key: "quality", label: "Quality", type: "text" },
      { key: "resolution", label: "Resolution", type: "text" },
    ],
  },
];

function parseArrayValue(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined && `${item}`.trim() !== "");
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed)
        ? parsed.filter((item) => item !== null && item !== undefined && `${item}`.trim() !== "")
        : [trimmed];
    } catch (error) {
      return trimmed.split(",").map((item) => item.trim()).filter(Boolean);
    }
  }

  if (value === null || value === undefined) {
    return [];
  }

  return [`${value}`];
}

function pickFirstValue(source, keys, fallback = null) {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }

  return fallback;
}

function normalizeBoolean(value) {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return value.toLowerCase() === "true" || value === "1";
  }

  return Boolean(value);
}

function normalizeNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
}

function normalizeType(value) {
  if (value === 1 || value === "GuardRail") {
    return "Guard Rail";
  }

  if (value === 0 || value === "Bot") {
    return "Bot";
  }

  return value || null;
}

export function normalizeGPTModel(model = {}) {
  // Auto-generate camelCase aliases for PascalCase keys (exported JSON format)
  const src = { ...model };
  for (const key of Object.keys(model)) {
    const camel = key.charAt(0).toLowerCase() + key.slice(1);
    if (camel !== key && !(camel in src)) {
      src[camel] = model[key];
    }
  }

  const historyMinute = pickFirstValue(src, ["historyMinute"]);
  const timeZone = pickFirstValue(src, ["timeZone"]);

  // Export format: Model = "gpt-5.2|gpt-5.2" (model|displayName)
  let modelValue = pickFirstValue(src, ["model"]);
  if (typeof modelValue === "string" && modelValue.includes("|")) {
    modelValue = modelValue.split("|")[0];
  }

  return {
    typeLLM: normalizeType(pickFirstValue(src, ["typeLLM", "typeName"])),
    modelId: pickFirstValue(src, ["modelId", "modelID"]),
    modelDisplay: pickFirstValue(src, ["modelDisplay"]),
    modelName: pickFirstValue(src, ["modelName"]),
    description: pickFirstValue(src, ["description"]),
    versioning: pickFirstValue(src, ["versioning"]),
    isPublish: normalizeBoolean(pickFirstValue(src, ["isPublish"])),
    provider: pickFirstValue(src, ["provider"]),
    model: modelValue,
    APIVersion: pickFirstValue(src, ["APIVersion", "apiVersion"]),
    extension: pickFirstValue(src, ["extension"]),
    modelType: pickFirstValue(src, ["modelType"]),
    reasoningEffort: pickFirstValue(src, ["reasoningEffort"]),
    endPointURL: pickFirstValue(src, ["endPointURL"]),
    apI_Key: pickFirstValue(src, ["apI_Key"]),
    promptTemplate: pickFirstValue(src, ["promptTemplate"], ""),
    jsonRequest: pickFirstValue(src, ["jsonRequest", "JsonRequest"]),
    stop: pickFirstValue(src, ["stop"]),
    gender: pickFirstValue(src, ["gender"]),
    temperature: normalizeNumber(pickFirstValue(src, ["temperature"])),
    topP: normalizeNumber(pickFirstValue(src, ["topP", "top_p"])),
    maxTokens: normalizeNumber(pickFirstValue(src, ["maxTokens", "max_tokens"])),
    frequencyPenalty: normalizeNumber(pickFirstValue(src, ["frequencyPenalty", "frequency_penalty"])),
    presencePenalty: normalizeNumber(pickFirstValue(src, ["presencePenalty", "presence_penalty"])),
    isStream: normalizeBoolean(pickFirstValue(src, ["isStream"])),
    isFeedback: normalizeBoolean(pickFirstValue(src, ["isFeedback"])),
    isAllowImage: normalizeBoolean(pickFirstValue(src, ["isAllowImage"])),
    fileUploadLimit: normalizeNumber(pickFirstValue(src, ["fileUploadLimit"])),
    isAllowLLMChange: normalizeBoolean(pickFirstValue(src, ["isAllowLLMChange"])),
    historyCount: normalizeNumber(pickFirstValue(src, ["historyCount"])),
    historyMinute: historyMinute === null || historyMinute === undefined || historyMinute === ""
      ? null
      : Math.abs(Number(historyMinute)),
    Len_ShortMemory: normalizeNumber(pickFirstValue(src, ["Len_ShortMemory", "len_ShortMemory"])),
    IsLongTerm_Memory: normalizeBoolean(pickFirstValue(src, ["IsLongTerm_Memory", "isLongTerm_Memory"])),
    IsChainMassage: normalizeBoolean(pickFirstValue(src, ["IsChainMassage", "isChainMassage"])),
    LenChainMassage: normalizeNumber(pickFirstValue(src, ["LenChainMassage", "lenChainMassage"])),
    TimeSecondChainMassage: normalizeNumber(pickFirstValue(src, ["TimeSecondChainMassage", "timeSecondChainMassage"])),
    maximumChatInput: normalizeNumber(pickFirstValue(src, ["maximumChatInput"])),
    typingSpeed: normalizeNumber(pickFirstValue(src, ["typingSpeed"])),
    vectordbName: parseArrayValue(pickFirstValue(src, ["vectordbName"])),
    topVDB: normalizeNumber(pickFirstValue(src, ["topVDB"])),
    Cosine: normalizeNumber(pickFirstValue(src, ["Cosine", "cosine"])),
    productGroupID: parseArrayValue(pickFirstValue(src, ["productGroupID"])),
    productTopVDB: normalizeNumber(pickFirstValue(src, ["productTopVDB"])),
    productCosine: normalizeNumber(pickFirstValue(src, ["productCosine"])),
    groupReplaceID: pickFirstValue(src, ["groupReplaceID"]),
    DataIntegrationID: pickFirstValue(src, ["DataIntegrationID", "dataIntegrationID"]),
    SqlModelID: pickFirstValue(src, ["SqlModelID", "sqlModelID"]),
    promptSqlNoData: pickFirstValue(src, ["promptSqlNoData"]),
    IntentGroupID: pickFirstValue(src, ["IntentGroupID", "intentGroupID"]),
    flowGroupID: pickFirstValue(src, ["flowGroupID"]),
    guardRailModelID: pickFirstValue(src, ["guardRailModelID", "GuardRailModelID"]),
    monitorLineUserID: pickFirstValue(src, ["monitorLineUserID", "monitorLineUserId"]),
    monitorSocialID: pickFirstValue(src, ["monitorSocialID", "monitorSocialId"]),
    isShowNameUserMonitor: normalizeBoolean(pickFirstValue(src, ["isShowNameUserMonitor"])),
    forbiddenWord: parseArrayValue(pickFirstValue(src, ["forbiddenWord"])),
    loadingBarFlag: normalizeBoolean(pickFirstValue(src, ["loadingBarFlag"])),
    loadingBarDuration: normalizeNumber(pickFirstValue(src, ["loadingBarDuration", "loadingBarTime"])),
    enableTime: normalizeBoolean(pickFirstValue(src, ["enableTime"], Boolean(timeZone))),
    timeZone,
    styleImage: pickFirstValue(src, ["styleImage"]),
    resFormatImage: pickFirstValue(src, ["resFormatImage"]),
    quality: pickFirstValue(src, ["quality"]),
    resolution: pickFirstValue(src, ["resolution"]),
  };
}

export function getComparableFields(excludedKeys = []) {
  return GPT_MODEL_COMPARE_SECTIONS.flatMap((section) =>
    section.fields
      .filter((field) => !excludedKeys.includes(field.key))
      .map((field) => ({ ...field, section: section.title }))
  );
}

export function areCompareValuesEqual(leftValue, rightValue) {
  return JSON.stringify(leftValue) === JSON.stringify(rightValue);
}

export function formatCompareValue(value, type) {
  if (type === "boolean") {
    return value ? "Yes" : "No";
  }

  if (type === "array") {
    return Array.isArray(value) && value.length > 0 ? value.join(", ") : DEFAULT_EMPTY_VALUE;
  }

  if (type === "list-text") {
    return Array.isArray(value) && value.length > 0 ? value.join("\n") : DEFAULT_EMPTY_VALUE;
  }

  if (type === "secret") {
    return value ? "Configured" : DEFAULT_EMPTY_VALUE;
  }

  if (value === null || value === undefined || value === "") {
    return DEFAULT_EMPTY_VALUE;
  }

  return `${value}`;
}

export function buildCompareSummary(leftModel, rightModel, excludedKeys = []) {
  const left = normalizeGPTModel(leftModel);
  const right = normalizeGPTModel(rightModel);

  return getComparableFields(excludedKeys)
    .filter((field) => !areCompareValuesEqual(left[field.key], right[field.key]))
    .map((field) => ({
      key: field.key,
      section: field.section,
      label: field.label,
      type: field.type,
      changed: true,
      leftValue: left[field.key],
      rightValue: right[field.key],
      leftDisplay: formatCompareValue(left[field.key], field.type),
      rightDisplay: formatCompareValue(right[field.key], field.type),
    }));
}

export function buildCompareSections(leftModel, rightModel, excludedKeys = []) {
  const left = normalizeGPTModel(leftModel);
  const right = normalizeGPTModel(rightModel);

  return GPT_MODEL_COMPARE_SECTIONS.map((section) => ({
    ...section,
    rows: section.fields
      .filter((field) => !excludedKeys.includes(field.key))
      .map((field) => ({
        ...field,
        leftValue: left[field.key],
        rightValue: right[field.key],
        leftDisplay: formatCompareValue(left[field.key], field.type),
        rightDisplay: formatCompareValue(right[field.key], field.type),
        changed: !areCompareValuesEqual(left[field.key], right[field.key]),
      })),
  })).filter((section) => section.rows.length > 0);
}